import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getMyReview, getReviews, saveMyReview } from "../utils/reviewsApi";

const EMPTY_OVERVIEW = { average_rating: 0, total_reviews: 0, reviews: [] };

function Stars({ value, interactive = false, onChange }) {
  return (
    <div className={`home-review-stars${interactive ? " is-interactive" : ""}`} aria-label={`${value} од 5 ѕвезди`}>
      {[1, 2, 3, 4, 5].map((star) =>
        interactive ? (
          <button
            type="button"
            key={star}
            className={star <= value ? "is-active" : ""}
            onClick={() => onChange(star)}
            aria-label={`Оцени со ${star} ѕвезди`}
          >
            ★
          </button>
        ) : (
          <span key={star} className={star <= Math.round(value) ? "is-active" : ""}>★</span>
        )
      )}
    </div>
  );
}

function HomeReviews() {
  const { authPayload } = useAuth();
  const signedIn = Boolean(authPayload?.id_token);
  const [overview, setOverview] = useState(EMPTY_OVERVIEW);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const loadReviews = async () => {
    const payload = await getReviews();
    setOverview(payload || EMPTY_OVERVIEW);
  };

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        const payload = await getReviews();
        if (!active) return;
        setOverview(payload || EMPTY_OVERVIEW);
        if (signedIn) {
          const mine = await getMyReview();
          if (!active || !mine?.review) return;
          setRating(mine.review.rating);
          setComment(mine.review.comment);
        }
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [signedIn]);

  const submitReview = async (event) => {
    event.preventDefault();
    const cleanComment = comment.trim();
    if (cleanComment.length < 10) {
      setError("Напишете барем 10 знаци за вашето искуство.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      setMessage("");
      await saveMyReview({ rating, comment: cleanComment });
      await loadReviews();
      setComment(cleanComment);
      setMessage("Ви благодариме! Вашата рецензија е зачувана.");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="home-reviews-section" id="reviews">
      <div className="home-reviews-heading">
        <div>
          <span className="home-reviews-eyebrow">Искуства од парови</span>
          <h2 className="section-title">Што велат нашите корисници</h2>
        </div>
        {overview.total_reviews > 0 ? (
          <div className="home-reviews-score">
            <strong>{Number(overview.average_rating).toFixed(1)}</strong>
            <div>
              <Stars value={overview.average_rating} />
              <span>{overview.total_reviews} рецензии</span>
            </div>
          </div>
        ) : null}
      </div>

      <div className="home-reviews-layout">
        <div className="home-review-list" aria-live="polite">
          {loading ? <p className="home-review-empty">Ги вчитуваме рецензиите…</p> : null}
          {!loading && overview.reviews?.length === 0 ? (
            <p className="home-review-empty">Бидете први што ќе го споделите своето искуство.</p>
          ) : null}
          {overview.reviews?.slice(0, 6).map((review) => (
            <article className="home-review-card" key={review.id}>
              <Stars value={review.rating} />
              <p>“{review.comment}”</p>
              <strong>{review.author_name}</strong>
            </article>
          ))}
        </div>

        <aside className="home-review-form-card">
          <h3>{comment ? "Ажурирајте ја рецензијата" : "Оценете ја Kazhav Da"}</h3>
          {signedIn ? (
            <form onSubmit={submitReview}>
              <label>Вашата оценка</label>
              <Stars value={rating} interactive onChange={setRating} />
              <label htmlFor="review-comment">Вашето искуство</label>
              <textarea
                id="review-comment"
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                maxLength={1000}
                rows={5}
                placeholder="Што ви помогна најмногу при организирањето?"
              />
              <span className="home-review-character-count">{comment.length}/1000</span>
              {error ? <p className="home-review-feedback is-error" role="alert">{error}</p> : null}
              {message ? <p className="home-review-feedback is-success" role="status">{message}</p> : null}
              <button className="home-review-submit" type="submit" disabled={saving}>
                {saving ? "Се зачувува…" : "Зачувај рецензија"}
              </button>
              <small>Можете да ја измените рецензијата во секое време.</small>
            </form>
          ) : (
            <div className="home-review-sign-in">
              <p>Само најавени корисници можат да остават рецензија.</p>
              <Link to="/login">Најавете се за да оцените</Link>
            </div>
          )}
        </aside>
      </div>
    </section>
  );
}

export default HomeReviews;
